import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG } from "./ui";
import { SITE, faqJsonLd } from "./seo";

/* ─── Hero ────────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* warm radial glow behind the headline */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-[-10%] h-[520px]"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.55) 0%, rgba(244,201,155,0) 70%)",
        }}
      />
      <Container className="relative pt-20 pb-10 text-center sm:pt-28">
        <div className="animate-fade-up">
          <Eyebrow>Now running in production</Eyebrow>
        </div>
        <h1 className="mx-auto mt-7 max-w-4xl text-balance text-5xl font-bold leading-[1.04] tracking-tight text-warm-ink animate-fade-up sm:text-6xl md:text-7xl">
          Rigorous interviews,{" "}
          <span className="font-display italic font-normal text-warm-accent">without a recruiter</span> in the loop.
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-warm-muted animate-fade-up">
          WonderfulInterview creates an AI video interviewer for any role, lets candidates self-schedule over
          email, conducts and proctors the conversation, then hands you an AI-written evaluation. You only
          meet the people worth meeting.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 animate-fade-up sm:flex-row">
          <MButton to="/signup" className="px-6 py-3 text-base">
            Start interviewing free <ArrowRight />
          </MButton>
          <MButton to="/platform" variant="outline" className="px-6 py-3 text-base">
            See how it works
          </MButton>
        </div>
        <p className="mt-4 text-sm text-warm-muted/80">Pay only for completed interviews · No seats · No subscription</p>
      </Container>

      {/* Framed alpine hero image with a floating evaluation card */}
      <Container className="relative pb-20">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-4xl border border-warm-border bg-warm-card shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
          <img
            src={IMG.hero}
            alt="Serene alpine peaks reflected in a still mountain lake at golden hour"
            className="h-[300px] w-full object-cover sm:h-[440px]"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-warm-ink/35 via-transparent to-transparent" />

          {/* floating score card */}
          <div className="absolute bottom-5 left-5 w-[260px] rounded-2xl border border-warm-border bg-warm-card/95 p-4 shadow-xl backdrop-blur sm:bottom-7 sm:left-7">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-warm-muted">Evaluation ready</span>
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                Strong Yes
              </span>
            </div>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-4xl font-bold tracking-tight text-warm-ink">87</span>
              <span className="pb-1 text-sm text-warm-muted">/ 100</span>
            </div>
            <div className="mt-3 space-y-1.5">
              {[
                ["Technical depth", 90],
                ["Communication", 84],
                ["Integrity", 100],
              ].map(([label, v]) => (
                <div key={label as string} className="flex items-center gap-2">
                  <span className="w-28 shrink-0 text-[11px] text-warm-muted">{label}</span>
                  <div className="h-1.5 flex-1 rounded-full bg-warm-sand">
                    <div className="h-1.5 rounded-full bg-warm-accent" style={{ width: `${v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* floating live badge */}
          <div className="absolute right-5 top-5 flex items-center gap-2 rounded-full border border-warm-border bg-warm-card/95 px-3 py-1.5 text-xs font-medium text-warm-ink shadow-lg backdrop-blur sm:right-7 sm:top-7">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warm-accent/60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-warm-accent" />
            </span>
            AI interview in progress
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─── Logo / trust strip ──────────────────────────────────────────────────── */
function TrustStrip() {
  const stats: [string, string][] = [
    ["3 min", "to publish an interview"],
    ["24/7", "candidate self-scheduling"],
    ["< 2 min", "to a scored report"],
    ["5 layers", "of integrity checks"],
  ];
  return (
    <section className="border-y border-warm-border bg-warm-sand/50">
      <Container className="grid grid-cols-2 gap-y-8 py-10 md:grid-cols-4">
        {stats.map(([big, small]) => (
          <div key={small} className="text-center">
            <div className="text-3xl font-bold tracking-tight text-warm-ink">{big}</div>
            <div className="mt-1 text-sm text-warm-muted">{small}</div>
          </div>
        ))}
      </Container>
    </section>
  );
}

/* ─── How it works ────────────────────────────────────────────────────────── */
function HowItWorks() {
  const steps: { n: string; title: string; body: string }[] = [
    {
      n: "01",
      title: "Design the interview",
      body: "Pick a type — technical, non-technical, or language — set the role, seniority and duration. We generate a rubric and a question plan, and quote the price before you send a single invite.",
    },
    {
      n: "02",
      title: "Invite & let email do the scheduling",
      body: "Add candidates and we email them. They simply reply with their availability — our AI reads the thread, negotiates a time, and locks a single-use join link inside the window.",
    },
    {
      n: "03",
      title: "Get a scored evaluation",
      body: "The candidate completes an AI video interview. Within minutes you receive an evaluation with dimension scores, strengths, concerns, a recommendation, the transcript, and the recording.",
    },
  ];
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            From open role to a hiring decision — on autopilot.
          </h2>
          <p className="mt-4 text-lg text-warm-muted">
            Scheduling, conducting, proctoring and scoring are all automated. Set it up once and it runs while you
            sleep.
          </p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div
              key={s.n}
              className="group rounded-3xl border border-warm-border bg-warm-card p-7 transition-shadow hover:shadow-[0_20px_50px_-25px_rgba(36,31,25,0.3)]"
            >
              <div className="font-display text-5xl italic text-warm-glow transition-colors group-hover:text-warm-accent">
                {s.n}
              </div>
              <h3 className="mt-5 text-xl font-semibold text-warm-ink">{s.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Feature grid ────────────────────────────────────────────────────────── */
function Icon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-6 w-6">
      <path d={path} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Features() {
  const features: { title: string; body: string; icon: string }[] = [
    {
      title: "AI video interviewer",
      body: "A lifelike AI interviewer asks adaptive follow-ups in real time and runs the whole conversation in the browser — no install for candidates.",
      icon: "M15 10l4.5-2.5v9L15 14M4 7h11v10H4z",
    },
    {
      title: "AI-written evaluations",
      body: "Every transcript is scored against your rubric with dimension scores, a summary, strengths, concerns and a clear recommendation.",
      icon: "M9 11l3 3 8-8M21 12a9 9 0 11-6.2-8.5",
    },
    {
      title: "Email-NLP scheduling",
      body: "Candidates negotiate a time in plain English. The platform parses replies and books a slot — zero calendars to chase.",
      icon: "M3 7l9 6 9-6M3 7v10h18V7M3 7h18",
    },
    {
      title: "Built-in integrity engine",
      body: "Gaze and multi-face perception, tab and paste monitoring, single-use tokens and transcript analysis surface evidence, never silent fails.",
      icon: "M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z M9 12l2 2 4-4",
    },
    {
      title: "Recordings in your control",
      body: "Each session is captured to private storage and served through signed links — a human-review fallback whenever you want one.",
      icon: "M15 10l4.5-2.5v9L15 14M4 7h11v10H4z",
    },
    {
      title: "Pay-as-you-go billing",
      body: "Add a card once. You're billed per completed interview at a transparent, duration-based price. No seats, no subscription, no waste.",
      icon: "M3 10h18M3 6h18v12H3zM7 15h4",
    },
  ];
  return (
    <section className="bg-warm-sand/40 py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>The platform</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Everything a hiring loop needs — minus the busywork.
          </h2>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-warm-border bg-warm-border sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="bg-warm-card p-7 transition-colors hover:bg-warm-glow/10">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-warm-accent/10 text-warm-accent">
                <Icon path={f.icon} />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-warm-ink">{f.title}</h3>
              <p className="mt-2.5 text-[15px] leading-relaxed text-warm-muted">{f.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Integrity split section ─────────────────────────────────────────────── */
function Integrity() {
  const signals: [string, string][] = [
    ["Perception flags", "Gaze-off-screen, multiple faces, and no-face events from the live video model."],
    ["Client telemetry", "Page-blur counts, paste events and devtools heuristics from the candidate's browser."],
    ["Single-use access", "Time-boxed, single-session join tokens — one candidate, one window, one link."],
    ["Transcript analysis", "Our AI flags reading-aloud cadence, prompt-injection attempts and coaching cues."],
  ];
  return (
    <section className="py-24">
      <Container className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <Eyebrow>Integrity, by design</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Trust the result, because the evidence is in the report.
          </h2>
          <p className="mt-4 text-lg text-warm-muted">
            Five independent layers of anti-malpractice signals are merged into every evaluation. The system never
            silently fails a candidate — it surfaces the evidence and lets you decide.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {signals.map(([t, d]) => (
              <div key={t}>
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <h3 className="font-semibold text-warm-ink">{t}</h3>
                </div>
                <p className="mt-1.5 pl-7 text-sm leading-relaxed text-warm-muted">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
            <img
              src={IMG.mist}
              alt="Mist drifting over alpine peaks at dawn"
              className="h-[460px] w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden w-56 rounded-2xl border border-warm-border bg-warm-card p-4 shadow-xl sm:block">
            <div className="text-xs font-medium uppercase tracking-wide text-warm-muted">Integrity score</div>
            <div className="mt-2 flex items-end gap-2">
              <span className="text-3xl font-bold text-emerald-600">Clean</span>
            </div>
            <p className="mt-1 text-xs text-warm-muted">0 flags · single face · no paste events</p>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─── Interview types ─────────────────────────────────────────────────────── */
function InterviewTypes() {
  const types: string[] = [
    "Technical",
    "Non-technical",
    "Behavioral",
    "Language",
    "Sales",
    "Customer support",
    "Leadership",
    "General screening",
  ];
  return (
    <section className="py-24">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <Eyebrow>One builder, every role</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Built for <span className="font-display italic text-warm-accent">every kind</span> of interview.
            </h2>
            <p className="mt-4 text-lg text-warm-muted">
              Pick a type, set the role, seniority, language and duration. We generate a weighted rubric and a
              structured question plan — then the AI interviewer adapts its follow-ups live.
            </p>
            <div className="mt-7">
              <MButton to="/platform" variant="outline">
                Explore the builder <ArrowRight />
              </MButton>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {types.map((t) => (
              <span
                key={t}
                className="rounded-full border border-warm-border bg-warm-card px-4 py-2.5 text-sm font-medium text-warm-ink"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ─── Pricing ─────────────────────────────────────────────────────────────── */
function Pricing() {
  const tiers: { mins: string; price: string; blurb: string; featured?: boolean }[] = [
    { mins: "15 min", price: "$17", blurb: "Quick screens & language checks" },
    { mins: "30 min", price: "$33.50", blurb: "Standard role interviews", featured: true },
    { mins: "45 min", price: "$50.50", blurb: "Deep technical conversations" },
    { mins: "60 min", price: "$67", blurb: "Senior & panel-grade loops" },
  ];
  return (
    <section id="pricing" className="bg-warm-ink py-24 text-warm-bg">
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-warm-bg/15 px-3 py-1 text-xs font-medium uppercase tracking-[0.14em] text-warm-bg/70">
            <span className="h-1.5 w-1.5 rounded-full bg-warm-glow" />
            Pay-as-you-go
          </span>
          <h2 className="mt-5 text-4xl font-bold tracking-tight sm:text-5xl">
            Priced by the minute. Billed only when complete.
          </h2>
          <p className="mt-4 text-lg text-warm-bg/60">
            One transparent price per interview, set by its duration and locked when you invite. No seats, no
            subscription — you pay when a candidate finishes.
          </p>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => (
            <div
              key={t.mins}
              className={`rounded-3xl border p-6 ${
                t.featured
                  ? "border-warm-accent bg-warm-accent/10 ring-1 ring-warm-accent"
                  : "border-warm-bg/12 bg-warm-bg/[0.04]"
              }`}
            >
              {t.featured && (
                <span className="mb-3 inline-block rounded-full bg-warm-accent px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                  Most popular
                </span>
              )}
              <div className="text-sm font-medium text-warm-bg/60">{t.mins} interview</div>
              <div className="mt-2 flex items-end gap-1">
                <span className="text-4xl font-bold tracking-tight">{t.price}</span>
                <span className="pb-1 text-sm text-warm-bg/50">/ interview</span>
              </div>
              <p className="mt-3 text-sm text-warm-bg/60">{t.blurb}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-col items-center gap-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <MButton to="/signup" className="px-6 py-3 text-base">
              Create your first interview <ArrowRight />
            </MButton>
            <MButton
              to="/pricing"
              variant="outline"
              className="border-warm-bg/30 bg-transparent px-6 py-3 text-base text-warm-bg hover:border-warm-bg"
            >
              See full pricing
            </MButton>
          </div>
          <p className="text-sm text-warm-bg/45">
            Prices include the AI interviewer, evaluation, scheduling and emails. Volume rates available.
          </p>
        </div>
      </Container>
    </section>
  );
}

/* ─── Closing CTA ─────────────────────────────────────────────────────────── */
function ClosingCTA() {
  return (
    <section className="py-24">
      <Container>
        <div className="relative overflow-hidden rounded-4xl border border-warm-border">
          <img src={IMG.lake} alt="Calm mountain lake at sunrise" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-warm-ink/85 to-warm-ink/45" />
          <div className="relative px-8 py-16 sm:px-16 sm:py-24">
            <h2 className="max-w-2xl text-4xl font-bold leading-tight tracking-tight text-warm-bg sm:text-5xl">
              Give every candidate a fair, rigorous interview.
            </h2>
            <p className="mt-4 max-w-xl text-lg text-warm-bg/75">
              Set up your first AI interview in minutes. No recruiter, no scheduling tetris, no bias — just a
              clear, evidence-backed read on every applicant.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <MButton to="/signup" className="px-6 py-3 text-base">
                Get started free <ArrowRight />
              </MButton>
              <MButton href="mailto:hello@wonderfulagent.ai" variant="outline" className="border-warm-bg/30 bg-transparent px-6 py-3 text-base text-warm-bg hover:border-warm-bg">
                Talk to us
              </MButton>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

const HOME_FAQS: { q: string; a: string }[] = [
  {
    q: "What is WonderfulInterview?",
    a: "A pay-as-you-go AI interview platform. You create an AI video interview for any role, candidates self-schedule by email, complete a recorded AI video interview, and you receive an AI-written evaluation with built-in integrity checks — no recruiter in the loop.",
  },
  {
    q: "How much does it cost?",
    a: "You pay per completed interview, priced by duration: $17 for 15 minutes, $33.50 for 30, $50.50 for 45, and $67 for 60. No seats and no subscription — you're billed only when a candidate completes and is evaluated.",
  },
  {
    q: "What kinds of interviews can I run?",
    a: "Eight types: technical, non-technical, behavioral, language, sales, customer support, leadership and general screening — each with a weighted rubric and a structured question plan.",
  },
  {
    q: "How do you prevent cheating?",
    a: "Five layers of integrity signals — video perception, client telemetry, single-use time-boxed join links, transcript analysis and private recordings — are merged into every evaluation as evidence for you to review.",
  },
];

const HOME_JSONLD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE.name,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: SITE.url,
  description:
    "Pay-as-you-go AI interview platform: AI video interviews, email self-scheduling, AI-written evaluations and a built-in integrity engine.",
  offers: {
    "@type": "Offer",
    price: "17.00",
    priceCurrency: "USD",
    description: "Per completed interview, from $17 (15 min). No seats, no subscription.",
  },
};

export function Home() {
  return (
    <MarketingPage
      seo={{
        title: "WonderfulInterview — AI interviews that run themselves",
        description:
          "Pay-as-you-go AI interview platform. Create AI video interviews, let candidates self-schedule by email, and get an AI-written evaluation with built-in integrity checks — no recruiter in the loop.",
        path: "/",
        jsonLd: [HOME_JSONLD, faqJsonLd(HOME_FAQS)],
      }}
    >
      <Hero />
      <TrustStrip />
      <HowItWorks />
      <Features />
      <Integrity />
      <InterviewTypes />
      <Pricing />
      <ClosingCTA />
    </MarketingPage>
  );
}
